/**
 * CompanyPageDetail — Individual company page view
 * Route: /app/business/pages/:slug
 *
 * Tabs: Posts · Products · People · About · Page Admin (super_admin only)
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate }       from 'react-router-dom';
import { TabView, TabPanel }            from 'primereact/tabview';
import { Button }                       from 'primereact/button';
import { Avatar }                       from 'primereact/avatar';
import { Tag }                          from 'primereact/tag';
import { Card }                         from 'primereact/card';
import { Skeleton }                     from 'primereact/skeleton';
import { Toast }                        from 'primereact/toast';
import { InputTextarea }                from 'primereact/inputtextarea';
import { InputText }                    from 'primereact/inputtext';
import { Dialog }                       from 'primereact/dialog';
import { Dropdown }                     from 'primereact/dropdown';
import { InputNumber }                  from 'primereact/inputnumber';
import { Divider }                      from 'primereact/divider';
import { Image }                        from 'primereact/image';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import companyPagesBusinessService      from '../../services/companyPagesBusinessService';
import { socialFeedService }            from '../../services/socialFeedService';

/* ── constants ─────────────────────────────────────────────────── */
const ENTITY_COLORS = {
    bank: '#BE185D', public_bank: '#0369A1', nbfc: '#B45309',
    fintech: '#7C3AED', dsa: '#0891B2', broker: '#0A66C2',
    legal: '#065F46', technical: '#1D4ED8',
};

const ROLE_META = {
    admin:       { label: 'Admin',       icon: 'pi-crown',     severity: 'warning'   },
    super_admin: { label: 'Super Admin', icon: 'pi-star-fill', severity: 'warning'   },
    editor:      { label: 'Editor',      icon: 'pi-pencil',    severity: 'info'      },
    viewer:      { label: 'Viewer',      icon: 'pi-eye',       severity: 'secondary' },
};

const LOAN_TYPE_META = {
    home_loan:          { label: 'Home Loan',           icon: 'pi-home'         },
    personal_loan:      { label: 'Personal Loan',       icon: 'pi-user'         },
    business_loan:      { label: 'Business Loan',       icon: 'pi-briefcase'    },
    education_loan:     { label: 'Education Loan',      icon: 'pi-book'         },
    auto_loan:          { label: 'Auto Loan',           icon: 'pi-car'          },
    gold_loan:          { label: 'Gold Loan',           icon: 'pi-star'         },
    agriculture_loan:   { label: 'Agriculture Loan',    icon: 'pi-sun'          },
    construction_loan:  { label: 'Construction Loan',   icon: 'pi-building'     },
};

const ROLE_OPTIONS = [
    { label: 'Viewer',      value: 'viewer'      },
    { label: 'Editor',      value: 'editor'      },
    { label: 'Super Admin', value: 'super_admin' },
];

const initials = name =>
    (name || '').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';

const fmtINR = v => v != null ? `₹${Number(v).toLocaleString('en-IN')}` : '—';

const formatTimeAgo = dateString => {
    if (!dateString) return '';
    const diff = Math.floor((Date.now() - new Date(dateString)) / 1000);
    if (diff < 60)    return 'just now';
    if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
};

/* ── video embed helper ─────────────────────────────────────────── */
const getVideoEmbedUrl = url => {
    if (!url) return null;
    const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]{11})/);
    if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
    const vi = url.match(/vimeo\.com\/(\d+)/);
    if (vi) return `https://player.vimeo.com/video/${vi[1]}`;
    return null;
};

const isVideoUrl = url => url && (url.includes('youtube') || url.includes('youtu.be') || url.includes('vimeo') || /\.(mp4|webm|ogg)$/i.test(url));

/* ── Single Post Card component ──────────────────────────────────── */
const PostCard = ({ post, page, color, canEdit, isSuperAdmin, onDelete }) => {
    const toast = useRef(null);
    const [liked,        setLiked]        = useState(post.is_liked  || false);
    const [saved,        setSaved]        = useState(post.is_saved  || false);
    const [likeCount,    setLikeCount]    = useState(post.like_count    || post.likes    || 0);
    const [commentCount, setCommentCount] = useState(post.comment_count || post.comments || 0);
    const [showComments, setShowComments] = useState(false);
    const [comments,     setComments]     = useState([]);
    const [commentText,  setCommentText]  = useState('');
    const [loadingCmt,   setLoadingCmt]   = useState(false);
    const [postingCmt,   setPostingCmt]   = useState(false);
    const [liking,       setLiking]       = useState(false);
    const [saving,       setSaving]       = useState(false);

    /* media: prefer media_urls_full (server-corrected URLs) over raw media_urls */
    const rawMedia  = post.media_urls_full || post.media_urls || post.media || [];
    const mediaList = rawMedia.map(m => typeof m === 'string' ? { type: isVideoUrl(m) ? 'video' : 'image', url: m } : m);
    const imageMedia = mediaList.filter(m => m.type === 'image' || (!m.type && !isVideoUrl(m.url)));
    const videoMedia = mediaList.filter(m => m.type === 'video' || isVideoUrl(m.url));

    /* can this viewer delete the post? */
    let currentUser = {};
    try { currentUser = JSON.parse(localStorage.getItem('user') || '{}'); } catch {}
    const isMyPost = post.author_profile_id && currentUser?.id && String(post.author_profile_id) === String(currentUser.id);
    const canDeletePost = isSuperAdmin || (canEdit && isMyPost);

    const toggleLike = async () => {
        if (liking) return;
        setLiking(true);
        const wasLiked = liked;
        setLiked(!wasLiked);
        setLikeCount(c => wasLiked ? Math.max(0, c - 1) : c + 1);
        try {
            if (wasLiked) await socialFeedService.removeReaction(post.id, 'like');
            else          await socialFeedService.addReaction(post.id, 'like');
        } catch {
            setLiked(wasLiked);
            setLikeCount(c => wasLiked ? c + 1 : Math.max(0, c - 1));
        } finally {
            setLiking(false);
        }
    };

    const toggleSave = async () => {
        if (saving) return;
        setSaving(true);
        const wasSaved = saved;
        setSaved(!wasSaved);
        try {
            if (wasSaved) await socialFeedService.removeReaction(post.id, 'save');
            else          await socialFeedService.addReaction(post.id, 'save');
        } catch {
            setSaved(wasSaved);
        } finally {
            setSaving(false);
        }
    };

    const loadComments = async () => {
        if (showComments) { setShowComments(false); return; }
        setShowComments(true);
        if (comments.length > 0) return;
        setLoadingCmt(true);
        try {
            const res = await socialFeedService.getComments(post.id);
            setComments(res?.success ? (res.data?.data || res.data || []) : []);
        } finally {
            setLoadingCmt(false);
        }
    };

    const submitComment = async () => {
        if (!commentText.trim() || postingCmt) return;
        setPostingCmt(true);
        try {
            const res = await socialFeedService.addComment(post.id, commentText.trim());
            if (res?.success) {
                const myName = currentUser?.name || currentUser?.full_name || 'You';
                const newComment = res.data || {
                    id: Date.now(),
                    comment_text: commentText.trim(),
                    created_at: new Date().toISOString(),
                    user_profile: { full_name: myName },
                    profile: { full_name: myName },
                };
                // Ensure the returned comment also has user_profile if only profile is present
                if (newComment.profile && !newComment.user_profile) {
                    newComment.user_profile = newComment.profile;
                }
                setComments(prev => [...prev, newComment]);
                setCommentCount(c => c + 1);
                setCommentText('');
            }
        } finally {
            setPostingCmt(false);
        }
    };

    const handleDelete = () => {
        confirmDialog({
            message: 'Delete this post? This cannot be undone.',
            header: 'Delete Post',
            icon: 'pi pi-trash',
            acceptLabel: 'Delete',
            rejectLabel: 'Cancel',
            acceptClassName: 'p-button-danger',
            accept: () => onDelete(post.id),
        });
    };

    const authorName = post.authorProfile?.full_name || post.author_profile?.full_name || post.author_name || post.author || 'Unknown';
    const authorPhoto = post.authorProfile?.profile_photo_url || post.author_profile?.profile_photo_url;

    return (
        <Card className="mb-3 border-1 surface-border shadow-1">
            {/* Author row */}
            <div className="flex align-items-start gap-3 mb-3">
                {authorPhoto ? (
                    <img src={authorPhoto} alt={authorName} style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                ) : (
                    <Avatar label={initials(authorName)} shape="circle" style={{ width: '40px', height: '40px', background: color, color: '#fff', fontSize: '13px', fontWeight: 700, flexShrink: 0 }} />
                )}
                <div className="flex-1">
                    <div className="font-semibold text-sm text-900">{authorName}</div>
                    <div className="text-xs text-400">{formatTimeAgo(post.created_at)}</div>
                </div>
                {canDeletePost && (
                    <Button
                        icon="pi pi-trash"
                        text
                        rounded
                        severity="danger"
                        size="small"
                        tooltip="Delete post"
                        tooltipOptions={{ position: 'top' }}
                        onClick={handleDelete}
                        style={{ width: '32px', height: '32px', flexShrink: 0 }}
                        aria-label="Delete post"
                    />
                )}
            </div>

            {/* Text content */}
            {(post.content || post.body) && (
                <p className="text-sm text-800 m-0 mb-3 line-height-3" style={{ whiteSpace: 'pre-wrap' }}>
                    {post.content || post.body}
                </p>
            )}

            {/* Images grid */}
            {imageMedia.length > 0 && (
                <div className="mb-3 border-round-lg overflow-hidden" style={{ display: 'grid', gridTemplateColumns: imageMedia.length === 1 ? '1fr' : imageMedia.length === 2 ? 'repeat(2,1fr)' : 'repeat(3,1fr)', gap: '2px' }}>
                    {imageMedia.slice(0, 9).map((img, i) => (
                        <div key={i} style={{ position: 'relative', paddingBottom: imageMedia.length === 1 ? '52%' : '100%', overflow: 'hidden' }}>
                            <Image
                                src={img.url || img}
                                alt={`image-${i}`}
                                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                imageStyle={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                preview
                            />
                            {i === 8 && imageMedia.length > 9 && (
                                <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '1.4rem', fontWeight: 700 }}>
                                    +{imageMedia.length - 9}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Video embeds */}
            {videoMedia.map((vid, i) => {
                const embedUrl = getVideoEmbedUrl(vid.url || vid);
                return (
                    <div key={i} className="mb-3">
                        {embedUrl ? (
                            <div style={{ position: 'relative', paddingBottom: '56.25%', borderRadius: '8px', overflow: 'hidden' }}>
                                <iframe src={embedUrl} title="video" frameBorder="0" allowFullScreen style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
                            </div>
                        ) : (
                            <a href={vid.url || vid} target="_blank" rel="noreferrer" className="flex align-items-center gap-2 p-2 border-1 surface-border border-round-lg surface-50 no-underline text-700 hover:surface-100 transition-all">
                                <i className="pi pi-video text-primary" />
                                <span className="text-sm flex-1 overflow-hidden text-overflow-ellipsis white-space-nowrap">{vid.url || vid}</span>
                                <i className="pi pi-external-link text-400 text-xs" />
                            </a>
                        )}
                    </div>
                );
            })}

            {/* Reaction counts row — like count and comment count side by side */}
            {(likeCount > 0 || commentCount > 0) && (
                <div className="flex align-items-center gap-3 text-xs text-400 mb-2">
                    {likeCount > 0 && (
                        <span className="flex align-items-center gap-1">
                            <i className="pi pi-thumbs-up text-primary" style={{ fontSize: '12px' }} />
                            <span>{likeCount} {likeCount === 1 ? 'like' : 'likes'}</span>
                        </span>
                    )}
                    {commentCount > 0 && (
                        <span className="flex align-items-center gap-1 cursor-pointer hover:text-primary" onClick={loadComments}>
                            <i className="pi pi-comments" style={{ fontSize: '12px' }} />
                            <span>{commentCount} {commentCount === 1 ? 'comment' : 'comments'}</span>
                        </span>
                    )}
                </div>
            )}

            {/* Action bar */}
            <div className="flex align-items-center gap-1 pt-2 border-top-1 surface-border">
                <Button
                    icon={`pi ${liked ? 'pi-thumbs-up-fill' : 'pi-thumbs-up'}`}
                    label={liked ? 'Liked' : 'Like'}
                    text
                    size="small"
                    className={`flex-1 justify-content-center ${liked ? 'text-primary font-semibold' : 'text-500'}`}
                    onClick={toggleLike}
                    loading={liking}
                />
                <Button
                    icon="pi pi-comments"
                    label={`Comment${commentCount > 0 ? ` (${commentCount})` : ''}`}
                    text
                    size="small"
                    className={`flex-1 justify-content-center ${showComments ? 'text-primary font-semibold' : 'text-500'}`}
                    onClick={loadComments}
                />
                <Button
                    icon={`pi ${saved ? 'pi-bookmark-fill' : 'pi-bookmark'}`}
                    label={saved ? 'Saved' : 'Save'}
                    text
                    size="small"
                    className={`flex-1 justify-content-center ${saved ? 'text-primary font-semibold' : 'text-500'}`}
                    onClick={toggleSave}
                    loading={saving}
                />
            </div>

            {/* Comments section */}
            {showComments && (
                <div className="mt-3 pt-3 border-top-1 surface-border">
                    {loadingCmt && <Skeleton height="2.5rem" className="mb-2" />}
                    {!loadingCmt && comments.length === 0 && (
                        <p className="text-xs text-400 text-center mb-3">No comments yet. Be the first!</p>
                    )}
                    {comments.map(c => {
                        const cmtAuthorName = c.user_profile?.full_name || c.profile?.full_name || c.author?.full_name || c.author?.name || c.author_name || c.user?.full_name || c.user?.name || 'Unknown User';
                        const cmtInitials   = initials(cmtAuthorName);
                        const cmtText       = c.comment_text || c.content || '';
                        return (
                            <div key={c.id} className="flex gap-2 mb-2">
                                <Avatar
                                    label={cmtInitials}
                                    shape="circle"
                                    style={{ width: '32px', height: '32px', background: 'var(--primary-color)', color: '#fff', fontSize: '11px', fontWeight: 700, flexShrink: 0 }}
                                />
                                <div className="flex-1">
                                    <div className="surface-50 border-round-lg px-3 py-2 border-1 surface-border">
                                        <div className="text-xs font-semibold text-900 mb-1">{cmtAuthorName}</div>
                                        <div className="text-sm text-800">{cmtText}</div>
                                    </div>
                                    <div className="text-xs text-400 mt-1 pl-2">{formatTimeAgo(c.created_at)}</div>
                                </div>
                            </div>
                        );
                    })}
                    {/* Add comment */}
                    <div className="flex gap-2 mt-2">
                        <Avatar
                            label={(() => { const n = currentUser?.name || currentUser?.full_name || 'Me'; return n.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || 'ME'; })()}
                            shape="circle"
                            style={{ width: '32px', height: '32px', background: color, color: '#fff', fontSize: '11px', fontWeight: 700, flexShrink: 0 }}
                        />
                        <div className="flex-1 flex gap-1">
                            <InputText
                                value={commentText}
                                onChange={e => setCommentText(e.target.value)}
                                placeholder="Write a comment…"
                                className="flex-1 p-inputtext-sm"
                                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitComment(); } }}
                            />
                            <Button
                                icon={postingCmt ? 'pi pi-spin pi-spinner' : 'pi pi-send'}
                                size="small"
                                disabled={!commentText.trim() || postingCmt}
                                onClick={submitComment}
                                style={{ width: '36px', height: '36px', padding: 0 }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </Card>
    );
};

/* ════════════════════════════════════════════════════════════════ */
const CompanyPageDetail = () => {
    const { slug }   = useParams();
    const navigate   = useNavigate();
    const toast      = useRef(null);
    const imageInputRef = useRef(null);

    const [page,         setPage]         = useState(null);
    const [posts,        setPosts]        = useState([]);
    const [products,     setProducts]     = useState([]);
    const [people,       setPeople]       = useState([]);
    const [loading,      setLoading]      = useState(true);
    const [activeTab,    setActiveTab]    = useState(0);
    const [following,    setFollowing]    = useState(false);

    /* compose state */
    const [postBody,       setPostBody]       = useState('');
    const [postImages,     setPostImages]     = useState([]); // {file, preview}
    const [postVideoUrl,   setPostVideoUrl]   = useState('');
    const [showVideoInput, setShowVideoInput] = useState(false);
    const [postingPost,    setPostingPost]    = useState(false);
    const [uploadingMedia, setUploadingMedia] = useState(false);

    /* posts filter */
    const [postSearch,     setPostSearch]     = useState('');
    const [postLoanType,   setPostLoanType]   = useState('');
    const [postSubProduct, setPostSubProduct] = useState('');

    /* product dialog */
    const [showProduct, setShowProduct] = useState(false);
    const [editProduct, setEditProduct] = useState(null);
    const [savingProd,  setSavingProd]  = useState(false);
    const [prodForm,    setProdForm]    = useState({});

    /* add member / editor access */
    const [showAddMember,     setShowAddMember]     = useState(false);
    const [selectedUser,      setSelectedUser]      = useState(null);
    const [addingMember,      setAddingMember]      = useState(false);
    const [adminUsers,        setAdminUsers]        = useState([]);
    const [adminUsersLoading, setAdminUsersLoading] = useState(false);
    const [adminSelectedUser, setAdminSelectedUser] = useState(null);
    const [addingUserEditor,  setAddingUserEditor]  = useState(false);

    /* ── load ─────────────────────────────────────────────────── */
    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [pgRes, postsRes, prodsRes, pplRes] = await Promise.allSettled([
                companyPagesBusinessService.getPage(slug),
                companyPagesBusinessService.getPosts(slug),
                companyPagesBusinessService.getProducts(slug),
                companyPagesBusinessService.getPeople(slug),
            ]);
            const pg = pgRes.status === 'fulfilled' && pgRes.value?.success ? pgRes.value.data : null;
            setPage(pg);
            setFollowing(pg?.is_following ?? false);
            setPosts(postsRes.status === 'fulfilled' && postsRes.value?.success
                ? (postsRes.value.data?.data || postsRes.value.data || []) : []);
            setProducts(prodsRes.status === 'fulfilled' && prodsRes.value?.success
                ? (prodsRes.value.data || []) : []);
            setPeople(pplRes.status === 'fulfilled' && pplRes.value?.success
                ? (pplRes.value.data?.data || pplRes.value.data || []) : []);
        } finally {
            setLoading(false);
        }
    }, [slug]);

    useEffect(() => { load(); }, [load]);

    /* cleanup previews on unmount */
    useEffect(() => {
        return () => { postImages.forEach(img => URL.revokeObjectURL(img.preview)); };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /* ── derived ──────────────────────────────────────────────── */
    const color        = ENTITY_COLORS[page?.entity_type] || '#0A66C2';
    const isSuperAdmin = page?.is_super_admin || page?.my_role === 'super_admin' || page?.my_role === 'admin';
    const canEdit      = page?.can_edit || isSuperAdmin || page?.my_role === 'editor';

    /* ── post filter derived options from this page's products ── */
    // Layer 1: unique loan types this company actually offers
    const loanTypeOptions = (() => {
        const seen = new Set();
        const opts = [{ label: 'All Products', value: '' }];
        products.forEach(p => {
            const types = Array.isArray(p.loan_type) ? p.loan_type
                : (p.loan_type ? p.loan_type.split(',') : []);
            types.forEach(t => {
                const trimmed = t.trim();
                if (trimmed && !seen.has(trimmed)) {
                    seen.add(trimmed);
                    const meta = LOAN_TYPE_META[trimmed];
                    opts.push({ label: meta ? meta.label : trimmed, value: trimmed });
                }
            });
        });
        return opts;
    })();

    // Layer 2: product names under the selected loan type
    const subProductOptions = (() => {
        if (!postLoanType) return [];
        const opts = [{ label: 'All Sub-Products', value: '' }];
        products.forEach(p => {
            const types = Array.isArray(p.loan_type) ? p.loan_type
                : (p.loan_type ? p.loan_type.split(',') : []);
            if (types.map(t => t.trim()).includes(postLoanType) && p.product_name) {
                opts.push({ label: p.product_name, value: p.product_name });
            }
        });
        return opts;
    })();

    // Client-side filtered posts
    const filteredPosts = posts.filter(post => {
        const body = (post.body || post.content || post.text || '').toLowerCase();
        if (postSearch && !body.includes(postSearch.toLowerCase())) return false;
        if (postLoanType) {
            const loanLabel = (LOAN_TYPE_META[postLoanType]?.label || postLoanType).toLowerCase();
            if (!body.includes(loanLabel) && !body.includes(postLoanType.replace('_', ' '))) return false;
        }
        if (postSubProduct && !body.toLowerCase().includes(postSubProduct.toLowerCase())) return false;
        return true;
    });
    const hasPostFilter = postSearch || postLoanType || postSubProduct;

    /* ── follow ───────────────────────────────────────────────── */
    const toggleFollow = async () => {
        setFollowing(f => !f);
        await companyPagesBusinessService.toggleFollow(slug);
    };

    /* ── image select ─────────────────────────────────────────── */
    const handleImageSelect = e => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        const valid = files.filter(f => f.type.startsWith('image/')).slice(0, 10 - postImages.length);
        setPostImages(prev => [...prev, ...valid.map(file => ({ file, preview: URL.createObjectURL(file) }))]);
        e.target.value = '';
    };

    const removePostImage = idx => {
        setPostImages(prev => {
            URL.revokeObjectURL(prev[idx].preview);
            return prev.filter((_, i) => i !== idx);
        });
    };

    /* ── submit post ──────────────────────────────────────────── */
    const submitPost = async () => {
        if (!postBody.trim() && postImages.length === 0 && !postVideoUrl.trim()) return;
        setPostingPost(true);
        if (postImages.length > 0) setUploadingMedia(true);
        try {
            const res = await companyPagesBusinessService.createPostWithMedia(
                slug,
                { content: postBody, videoUrl: postVideoUrl || undefined },
                postImages.map(img => img.file)
            );
            if (res?.success === false) {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: res.message || 'Could not post.', life: 4000 });
                return;
            }
            const newPost = res?.data || {
                id: Date.now(),
                content: postBody,
                media_urls: postImages.length > 0 ? postImages.map(i => i.preview) : undefined,
                created_at: new Date().toISOString(),
                like_count: 0, comment_count: 0, is_liked: false, is_saved: false,
            };
            setPosts(prev => [newPost, ...prev]);
            setPostBody('');
            postImages.forEach(img => URL.revokeObjectURL(img.preview));
            setPostImages([]);
            setPostVideoUrl('');
            setShowVideoInput(false);
            toast.current?.show({ severity: 'success', summary: 'Posted!', detail: 'Your update is live.', life: 3000 });
        } finally {
            setPostingPost(false);
            setUploadingMedia(false);
        }
    };

    /* ── delete post ──────────────────────────────────────────── */
    const handleDeletePost = async postId => {
        const res = await companyPagesBusinessService.deletePost(slug, postId);
        if (res?.success === false) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: res.message || 'Could not delete post.', life: 3000 });
        } else {
            setPosts(prev => prev.filter(p => p.id !== postId));
            toast.current?.show({ severity: 'success', summary: 'Deleted', detail: 'Post removed.', life: 2500 });
        }
    };

    /* ── product CRUD ─────────────────────────────────────────── */
    const openProductForm = (prod = null) => {
        setEditProduct(prod);
        setProdForm(prod ? { ...prod } : { loan_type: 'home_loan', product_name: '', min_amount: null, max_amount: null, min_roi: null, max_roi: null, min_tenure: null, max_tenure: null });
        setShowProduct(true);
    };

    const saveProduct = async () => {
        setSavingProd(true);
        try {
            let res;
            if (editProduct?.id) {
                res = await companyPagesBusinessService.updateProduct(slug, editProduct.id, prodForm);
                if (res?.success !== false) setProducts(prev => prev.map(p => p.id === editProduct.id ? (res.data || { ...p, ...prodForm }) : p));
            } else {
                res = await companyPagesBusinessService.createProduct(slug, prodForm);
                setProducts(prev => [...prev, res?.success ? res.data : { id: Date.now(), ...prodForm }]);
            }
            setShowProduct(false);
            toast.current?.show({ severity: 'success', summary: 'Saved', life: 3000 });
        } finally {
            setSavingProd(false);
        }
    };

    const deleteProduct = id => {
        confirmDialog({
            message: 'Delete this loan product?', header: 'Confirm Delete',
            icon: 'pi pi-exclamation-triangle', acceptClassName: 'p-button-danger',
            accept: async () => {
                await companyPagesBusinessService.deleteProduct(slug, id);
                setProducts(prev => prev.filter(p => p.id !== id));
                toast.current?.show({ severity: 'success', summary: 'Deleted', life: 2500 });
            }
        });
    };

    /* ── load users for editor-access panel (super_admin only) ── */
    useEffect(() => {
        if (!page || page.my_role !== 'super_admin') return;
        setAdminUsersLoading(true);
        companyPagesBusinessService.getTenantUsers({ limit: 200 }).then(res => {
            const rows = Array.isArray(res?.items) ? res.items
                       : Array.isArray(res?.data)  ? res.data
                       : [];
            setAdminUsers(rows.map(user => {
                const nameStr = user.full_name || user.name ||
                    [user.first_name, user.last_name].filter(Boolean).join(' ').trim() ||
                    user.email || '';
                return { ...user, _display: nameStr, _sub: user.email || '', _role: user.primary_role || '' };
            }));
        }).catch(() => setAdminUsers([]))
          .finally(() => setAdminUsersLoading(false));
    }, [page?.my_role]); // eslint-disable-line react-hooks/exhaustive-deps

    /* Add a user as editor (inline card handler) */
    const addUserAsEditor = async () => {
        if (!adminSelectedUser) return;
        setAddingUserEditor(true);
        try {
            const nameStr = adminSelectedUser._display;
            const res = await companyPagesBusinessService.addManager(slug, adminSelectedUser.id, 'editor');
            if (res?.success === false) {
                toast.current?.show({ severity: 'error', summary: 'Failed', detail: res.message || 'Could not add editor. Please try again.', life: 4000 });
                return;
            }
            setPeople(prev => [...prev, { id: adminSelectedUser.id, profile_id: adminSelectedUser.id, name: nameStr, email: adminSelectedUser._sub || '', role: 'editor' }]);
            setAdminSelectedUser(null);
            toast.current?.show({ severity: 'success', summary: 'Editor Added', detail: `${nameStr} can now post on this page.`, life: 3000 });
        } finally {
            setAddingUserEditor(false);
        }
    };

    /* Legacy add member (dialog) — kept for compatibility */
    const addMember = async () => {
        if (!selectedUser) return;
        setAddingMember(true);
        try {
            const nameStr = selectedUser.name || `${selectedUser.first_name || ''} ${selectedUser.last_name || ''}`.trim();
            const res = await companyPagesBusinessService.addManager(slug, selectedUser.id || selectedUser.profile_id, 'editor');
            if (res?.success === false) {
                toast.current?.show({ severity: 'error', summary: 'Failed', detail: res.message || 'Could not add editor.', life: 4000 });
                return;
            }
            setPeople(prev => [...prev, { id: selectedUser.id, profile_id: selectedUser.id, name: nameStr, role: 'editor' }]);
            setShowAddMember(false); setSelectedUser(null);
            toast.current?.show({ severity: 'success', summary: 'Added', detail: `${nameStr} added.`, life: 3000 });
        } finally {
            setAddingMember(false);
        }
    };

    const removeMember = member => {
        confirmDialog({
            message: `Remove ${member.name || member.full_name} from this page?`, header: 'Remove Member',
            icon: 'pi pi-exclamation-triangle', acceptClassName: 'p-button-danger',
            accept: async () => {
                await companyPagesBusinessService.removeManager(slug, member.manager_id || member.profile_id || member.id);
                setPeople(prev => prev.filter(p => (p.profile_id || p.id) !== (member.profile_id || member.id)));
                toast.current?.show({ severity: 'success', summary: 'Removed', life: 2500 });
            }
        });
    };

    /* ── page admin actions ───────────────────────────────────── */
    const handleDeactivate = () => {
        confirmDialog({
            message: 'Deactivating will hide this page from the network. You can reactivate it later.',
            header: 'Deactivate Page?', icon: 'pi pi-ban',
            acceptLabel: 'Deactivate', rejectLabel: 'Cancel', acceptClassName: 'p-button-warning',
            accept: async () => {
                const res = await companyPagesBusinessService.deactivatePage(slug);
                toast.current?.show({ severity: res?.success === false ? 'warn' : 'info', summary: res?.success === false ? 'Note' : 'Deactivated', detail: res?.message || 'Page hidden from network.', life: 4000 });
            }
        });
    };

    const handleDeletePage = () => {
        confirmDialog({
            message: 'This will permanently delete the company page and all its content. This action cannot be undone.',
            header: 'Delete Company Page?', icon: 'pi pi-trash',
            acceptLabel: 'Delete Permanently', rejectLabel: 'Cancel', acceptClassName: 'p-button-danger',
            accept: async () => {
                const res = await companyPagesBusinessService.deletePage(slug);
                if (res?.success === false) {
                    toast.current?.show({ severity: 'error', summary: 'Error', detail: res.message || 'Could not delete page.', life: 4000 });
                } else {
                    toast.current?.show({ severity: 'success', summary: 'Deleted', detail: 'Company page deleted.', life: 2500 });
                    setTimeout(() => navigate('/app/business/pages'), 1500);
                }
            }
        });
    };

    /* ── loading skeleton ─────────────────────────────────────── */
    if (loading) {
        return (
            <div>
                <Skeleton height="180px" />
                <div className="px-4 pt-0" style={{ marginTop: '-36px' }}>
                    <div className="flex align-items-end gap-3 mb-3">
                        <Skeleton shape="square" size="72px" style={{ borderRadius: '14px', border: '4px solid var(--surface-card)' }} />
                        <div className="flex-1 pb-1"><Skeleton height="1.2rem" width="40%" className="mb-2" /><Skeleton height=".8rem" width="60%" /></div>
                    </div>
                    <Skeleton height="2.5rem" borderRadius="8px" />
                </div>
            </div>
        );
    }

    if (!page) return <div className="p-5 text-center text-400">Page not found.</div>;

    /* ── main render ──────────────────────────────────────────── */
    return (
        <div>
            <Toast ref={toast} />
            <ConfirmDialog />

            {/* ══ Banner ════════════════════════════════════════ */}
            <div style={{ position: 'relative', height: '180px', background: `linear-gradient(135deg, ${color} 0%, ${color}99 100%)`, overflow: 'hidden' }}>
                {/* back button — always on top */}
                <div style={{ position: 'absolute', top: '14px', left: '14px', zIndex: 20 }}>
                    <Button
                        icon="pi pi-arrow-left"
                        style={{ color: '#fff', background: 'rgba(0,0,0,.4)', border: 'none', borderRadius: '8px', width: '36px', height: '36px', padding: 0, backdropFilter: 'blur(6px)' }}
                        onClick={() => navigate('/app/business/pages')}
                        tooltip="Back to For Business"
                        tooltipOptions={{ position: 'right' }}
                    />
                </div>
            </div>

            {/* ══ Page header below banner ══════════════════════ */}
            <div className="px-3 md:px-4">
                {/* Avatar row — overlaps banner */}
                <div className="flex justify-content-between align-items-start flex-wrap gap-2" style={{ marginTop: '-36px', paddingBottom: '8px' }}>
                    <Avatar
                        label={initials(page.name)}
                        style={{ width: '72px', height: '72px', background: color, color: '#fff', fontSize: '24px', fontWeight: 800, borderRadius: '14px', border: '4px solid var(--surface-card)', flexShrink: 0, zIndex: 5, position: 'relative' }}
                    />
                    <div className="flex gap-2 align-items-center flex-wrap" style={{ paddingTop: '40px' }}>
                        {isSuperAdmin && (
                            <Button label="Edit Page" icon="pi pi-pencil" size="small" outlined onClick={() => toast.current?.show({ severity: 'info', summary: 'Coming Soon', life: 3000 })} />
                        )}
                    </div>
                </div>
                {/* Name + tagline clearly below avatar */}
                <div className="mb-2">
                    <h1 className="text-xl font-bold text-900 m-0 mb-1">{page.name}</h1>
                    {page.tagline && <p className="text-sm text-500 m-0">{page.tagline}</p>}
                </div>

                {/* Stats row */}
                <div className="flex flex-wrap gap-4 text-sm text-500 pb-3">
                    <span><i className="pi pi-users mr-1" />{(page.follower_count ?? 0).toLocaleString('en-IN')} followers</span>
                    {page.hq_city && <span><i className="pi pi-map-marker mr-1" />{page.hq_city}{page.hq_state ? `, ${page.hq_state}` : ''}</span>}
                    {page.industry && <span><i className="pi pi-briefcase mr-1" />{page.industry}</span>}
                    {page.website && <span><i className="pi pi-link mr-1" /><a href={page.website} target="_blank" rel="noreferrer" className="text-primary no-underline hover:underline">{page.website.replace(/^https?:\/\//, '')}</a></span>}
                </div>

                {/* Viewer-only access notice */}
                {!canEdit && page?.my_role && (
                    <div className="flex align-items-start gap-2 p-3 mb-2 border-round-lg" style={{ background: 'var(--blue-50)', border: '1px solid var(--blue-100)' }}>
                        <i className="pi pi-eye text-primary mt-1" style={{ fontSize: '14px', flexShrink: 0 }} />
                        <div>
                            <div className="text-sm font-semibold text-primary mb-1">View-Only Access</div>
                            <div className="text-xs text-600 line-height-3">
                                You can read posts, view products, and follow this page — but you cannot create or modify content. Contact the page admin to request Editor access.
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* ══ Tabs ══════════════════════════════════════════ */}
            <TabView activeIndex={activeTab} onTabChange={e => setActiveTab(e.index)} className="px-0">

                {/* ── Posts ─────────────────────────────────── */}
                <TabPanel header="Posts" leftIcon="pi pi-file-edit mr-2"
                    headerTemplate={(options) => (
                        <button type="button" className={options.className} onClick={options.onClick} data-testid="tab-overview" role="tab">
                            <i className="pi pi-file-edit mr-2"></i><span className={options.titleClassName}>Posts</span>
                        </button>
                    )}>
                    <div className="px-3 md:px-4 py-2" style={{ maxWidth: '680px' }}>

                        {/* Compose */}
                        {canEdit && (
                            <Card className="mb-4 border-1 surface-border shadow-1">
                                <div className="flex align-items-start gap-3 mb-3">
                                    <Avatar label={initials(page.name)} style={{ width: '40px', height: '40px', background: color, color: '#fff', fontSize: '13px', fontWeight: 800, borderRadius: '50%', flexShrink: 0 }} />
                                    <InputTextarea
                                        value={postBody}
                                        onChange={e => setPostBody(e.target.value)}
                                        placeholder="Share an update, announcement, or insight…"
                                        rows={3}
                                        className="w-full"
                                        autoResize
                                    />
                                </div>

                                {/* Image previews */}
                                {postImages.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mb-3 pl-5">
                                        {postImages.map((img, idx) => (
                                            <div key={idx} style={{ position: 'relative' }}>
                                                <img src={img.preview} alt="" style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', border: '1px solid var(--surface-border)', display: 'block' }} />
                                                <button
                                                    onClick={() => removePostImage(idx)}
                                                    style={{ position: 'absolute', top: '-6px', right: '-6px', width: '20px', height: '20px', background: 'var(--red-500)', border: 'none', borderRadius: '50%', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                                                >
                                                    <i className="pi pi-times" style={{ fontSize: '9px' }} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Video URL bar */}
                                {postVideoUrl && (
                                    <div className="flex align-items-center gap-2 p-2 border-1 surface-border border-round-lg mb-3 surface-50 ml-5">
                                        <i className="pi pi-video text-primary" />
                                        <span className="text-sm text-700 flex-1 overflow-hidden text-overflow-ellipsis white-space-nowrap">{postVideoUrl}</span>
                                        <Button icon="pi pi-times" text size="small" severity="danger" style={{ width: '24px', height: '24px', padding: 0 }} onClick={() => setPostVideoUrl('')} />
                                    </div>
                                )}

                                {/* Video URL input */}
                                {showVideoInput && !postVideoUrl && (
                                    <div className="flex gap-2 mb-3 ml-5">
                                        <InputText
                                            value={postVideoUrl}
                                            onChange={e => setPostVideoUrl(e.target.value)}
                                            placeholder="Paste YouTube, Vimeo, or any video URL…"
                                            className="flex-1 p-inputtext-sm"
                                            autoFocus
                                            onKeyDown={e => { if (e.key === 'Enter') setShowVideoInput(false); }}
                                        />
                                        <Button icon="pi pi-check" size="small" onClick={() => setShowVideoInput(false)} disabled={!postVideoUrl.trim()} />
                                        <Button icon="pi pi-times" size="small" text severity="danger" onClick={() => { setShowVideoInput(false); setPostVideoUrl(''); }} />
                                    </div>
                                )}

                                <Divider className="my-2" />

                                <div className="flex align-items-center justify-content-between">
                                    <div className="flex gap-1">
                                        <Button
                                            icon="pi pi-image"
                                            label="Photo"
                                            text size="small"
                                            className="text-500 hover:text-primary"
                                            onClick={() => imageInputRef.current?.click()}
                                            disabled={postImages.length >= 10}
                                        />
                                        <Button
                                            icon="pi pi-video"
                                            label="Video"
                                            text size="small"
                                            className={`${showVideoInput || postVideoUrl ? 'text-primary' : 'text-500 hover:text-primary'}`}
                                            onClick={() => { setShowVideoInput(v => !v); }}
                                        />
                                    </div>
                                    <Button
                                        label={uploadingMedia ? 'Uploading…' : postingPost ? 'Posting…' : 'Post'}
                                        icon={postingPost || uploadingMedia ? 'pi pi-spin pi-spinner' : 'pi pi-send'}
                                        size="small"
                                        disabled={(!postBody.trim() && postImages.length === 0 && !postVideoUrl.trim()) || postingPost}
                                        onClick={submitPost}
                                    />
                                </div>
                                <input ref={imageInputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/gif" multiple hidden onChange={handleImageSelect} />
                            </Card>
                        )}

                        {/* ── Post Filter Bar ── */}
                        {posts.length > 0 && (
                            <div className="flex flex-wrap align-items-center gap-2 mb-3 p-3 surface-50 border-1 surface-border border-round-xl">
                                <i className="pi pi-filter text-500 text-sm" />
                                {/* Text search */}
                                <span className="p-input-icon-left flex-1" style={{ minWidth: '150px' }}>
                                    <i className="pi pi-search" />
                                    <InputText
                                        value={postSearch}
                                        onChange={e => setPostSearch(e.target.value)}
                                        placeholder="Search posts…"
                                        className="p-inputtext-sm w-full"
                                    />
                                </span>
                                {/* Layer 1: Product type */}
                                {loanTypeOptions.length > 1 && (
                                    <Dropdown
                                        value={postLoanType}
                                        options={loanTypeOptions}
                                        onChange={e => { setPostLoanType(e.value); setPostSubProduct(''); }}
                                        placeholder="All Products"
                                        className="p-inputtext-sm"
                                        style={{ minWidth: '150px' }}
                                    />
                                )}
                                {/* Layer 2: Sub-product — only when a product is selected */}
                                {postLoanType && subProductOptions.length > 1 && (
                                    <Dropdown
                                        value={postSubProduct}
                                        options={subProductOptions}
                                        onChange={e => setPostSubProduct(e.value)}
                                        placeholder="All Sub-Products"
                                        className="p-inputtext-sm"
                                        style={{ minWidth: '170px' }}
                                    />
                                )}
                                {/* Clear button */}
                                {hasPostFilter && (
                                    <Button
                                        icon="pi pi-times"
                                        size="small"
                                        severity="secondary"
                                        outlined
                                        tooltip="Clear filters"
                                        tooltipOptions={{ position: 'top' }}
                                        onClick={() => { setPostSearch(''); setPostLoanType(''); setPostSubProduct(''); }}
                                        style={{ flexShrink: 0 }}
                                    />
                                )}
                            </div>
                        )}

                        {/* Posts list */}
                        {posts.length === 0 && (
                            <div className="text-center py-6 text-400">
                                <i className="pi pi-file-edit text-5xl mb-3 block" style={{ opacity: 0.3 }} />
                                <p className="m-0 font-medium">No posts yet.</p>
                                {canEdit && <p className="m-0 text-sm mt-1">Share the first update above!</p>}
                            </div>
                        )}

                        {hasPostFilter && filteredPosts.length === 0 && posts.length > 0 && (
                            <div className="text-center py-5 text-400">
                                <i className="pi pi-search text-4xl mb-3 block" style={{ opacity: 0.3 }} />
                                <p className="m-0 font-medium">No posts match your filter.</p>
                                <Button label="Clear filters" text size="small" className="mt-2" onClick={() => { setPostSearch(''); setPostLoanType(''); setPostSubProduct(''); }} />
                            </div>
                        )}

                        {filteredPosts.map(post => (
                            <PostCard
                                key={post.id}
                                post={post}
                                page={page}
                                color={color}
                                canEdit={canEdit}
                                isSuperAdmin={isSuperAdmin}
                                onDelete={handleDeletePost}
                            />
                        ))}
                    </div>
                </TabPanel>

                {/* ── Products ──────────────────────────────── */}
                <TabPanel header="Products" leftIcon="pi pi-th-large mr-2">
                    <div className="px-3 md:px-4 py-2">
                        {canEdit && (
                            <div className="flex justify-content-end mb-3">
                                <Button label="Add Product" icon="pi pi-plus" size="small" onClick={() => openProductForm()} />
                            </div>
                        )}
                        {products.length === 0 && (
                            <div className="text-center py-5 text-400">
                                <i className="pi pi-th-large text-4xl mb-3 block" style={{ opacity: 0.3 }} />
                                <p className="m-0">No loan products listed yet.</p>
                            </div>
                        )}
                        <div className="grid">
                            {products.map(prod => {
                                const meta = LOAN_TYPE_META[prod.loan_type] || { label: prod.loan_type, icon: 'pi-file' };
                                return (
                                    <div key={prod.id} className="col-12 md:col-6 lg:col-4">
                                        <Card className="h-full border-1 surface-border hover:shadow-3 transition-all transition-duration-200">
                                            <div className="flex align-items-start justify-content-between mb-2">
                                                <div className="flex align-items-center gap-2">
                                                    <div className="flex align-items-center justify-content-center border-round" style={{ width: '36px', height: '36px', background: `${color}18` }}>
                                                        <i className={`pi ${meta.icon} text-lg`} style={{ color }} />
                                                    </div>
                                                    <div>
                                                        <div className="font-bold text-sm text-900">{prod.product_name || prod.name}</div>
                                                        <div className="text-xs text-400">{meta.label}</div>
                                                    </div>
                                                </div>
                                                {canEdit && (
                                                    <div className="flex gap-1">
                                                        <Button icon="pi pi-pencil" size="small" text rounded onClick={() => openProductForm(prod)} />
                                                        <Button icon="pi pi-trash" size="small" text rounded severity="danger" onClick={() => deleteProduct(prod.id)} />
                                                    </div>
                                                )}
                                            </div>
                                            <div className="grid text-xs text-600 mt-2">
                                                <div className="col-6"><div className="text-400 mb-1">Loan Amount</div><div className="font-medium">{fmtINR(prod.min_amount)} – {fmtINR(prod.max_amount)}</div></div>
                                                <div className="col-6"><div className="text-400 mb-1">Interest Rate</div><div className="font-medium">{prod.min_roi}% – {prod.max_roi}% p.a.</div></div>
                                                <div className="col-6"><div className="text-400 mb-1">Tenure</div><div className="font-medium">{prod.min_tenure} – {prod.max_tenure} months</div></div>
                                                {prod.channels?.length > 0 && <div className="col-6"><div className="text-400 mb-1">Channels</div><div className="font-medium">{prod.channels.join(', ')}</div></div>}
                                            </div>
                                        </Card>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </TabPanel>

                {/* ── People ────────────────────────────────── */}
                <TabPanel header="People" leftIcon="pi pi-users mr-2">
                    <div className="px-3 md:px-4 py-2" style={{ maxWidth: '640px' }}>
                        <div className="text-xs font-bold text-500 uppercase mb-3" style={{ letterSpacing: '.6px' }}>
                            {people.length} {people.length === 1 ? 'Person' : 'People'} associated
                        </div>
                        {people.length === 0 ? (
                            <div className="text-center py-6 text-400">
                                <i className="pi pi-users text-5xl mb-3 block" style={{ opacity: 0.3 }} />
                                <p className="m-0 mb-1">No agents associated yet.</p>
                                <p className="m-0 text-xs" style={{ opacity: 0.7 }}>Agents can appear here by selecting this company under "Companies I Work With" in their profile.</p>
                            </div>
                        ) : (
                            <div className="flex flex-column gap-2">
                                {people.map(member => {
                                    const displayName = member.full_name || member.name || '';
                                    return (
                                        <div key={member.id} className="flex align-items-center gap-3 p-3 surface-card border-1 surface-border border-round-lg">
                                            <Avatar
                                                label={initials(displayName)}
                                                image={member.profile_photo_url || null}
                                                shape="circle"
                                                style={{ width: '40px', height: '40px', background: 'var(--primary-100)', color: 'var(--primary-700)', fontSize: '13px', fontWeight: 700, flexShrink: 0 }}
                                            />
                                            <div className="flex-1 min-w-0">
                                                <div className="font-semibold text-sm text-900">{displayName}</div>
                                                <div className="text-xs text-500">
                                                    {member.current_designation || member.email || ''}
                                                    {member.location_city ? <span className="mx-1">·</span> : ''}
                                                    {member.location_city || ''}
                                                </div>
                                            </div>
                                            {member.page_role && member.page_role !== 'super_admin' && member.page_role !== 'member' && (
                                                <Tag value={ROLE_META[member.page_role]?.label || member.page_role} severity={ROLE_META[member.page_role]?.severity || 'secondary'} className="text-xs" />
                                            )}
                                            {member.is_verified && (
                                                <i className="pi pi-verified text-primary" style={{ fontSize: '0.85rem' }} title="Verified" />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </TabPanel>

                {/* ── About ─────────────────────────────────── */}
                <TabPanel header="About" leftIcon="pi pi-info-circle mr-2">
                    <div className="px-3 md:px-4 py-2" style={{ maxWidth: '600px' }}>
                        <Card className="border-1 surface-border">
                            <div className="grid">
                                {[
                                    { label: 'Entity Type',   value: page.entity_type?.replace('_', ' ') },
                                    { label: 'Industry',      value: page.industry      },
                                    { label: 'Headquartered', value: [page.hq_city, page.hq_state].filter(Boolean).join(', ') },
                                    { label: 'Website',       value: page.website, link: true },
                                    { label: 'RBI License',   value: page.rbi_license   },
                                    { label: 'Legal Name',    value: page.legal_name     },
                                ].filter(r => r.value).map(row => (
                                    <div key={row.label} className="col-12 md:col-6 mb-3">
                                        <div className="text-xs text-400 mb-1 uppercase font-medium" style={{ letterSpacing: '.4px' }}>{row.label}</div>
                                        {row.link
                                            ? <a href={row.value} target="_blank" rel="noreferrer" className="text-primary text-sm no-underline hover:underline">{row.value}</a>
                                            : <div className="text-sm font-medium text-800 capitalize">{row.value}</div>
                                        }
                                    </div>
                                ))}
                            </div>
                            {page.description && (
                                <>
                                    <Divider />
                                    <div className="text-xs text-400 mb-2 uppercase font-medium" style={{ letterSpacing: '.4px' }}>About</div>
                                    <p className="text-sm text-700 m-0 line-height-3">{page.description}</p>
                                </>
                            )}
                        </Card>
                    </div>
                </TabPanel>

                {/* ── Page Admin (super_admin only) ──────────── */}
                {isSuperAdmin && (
                    <TabPanel header="Page Admin" leftIcon="pi pi-cog mr-2">
                        <div className="px-3 md:px-4 py-3" style={{ maxWidth: '520px' }}>

                            {/* Team & Editor Access */}
                            <Card className="border-1 surface-border mb-3">
                                <div className="text-base font-bold text-900 mb-1">
                                    <i className="pi pi-users mr-2 text-primary" />Team &amp; Editor Access
                                </div>
                                <p className="text-sm text-500 mt-1 mb-3 line-height-3">
                                    Grant <strong>Editor</strong> access to your leads — they can create posts and manage content on this page.
                                </p>

                                {/* Current editors */}
                                {people.filter(p => p.role === 'editor' || p.role === 'super_admin').length > 0 && (
                                    <div className="mb-3">
                                        <div className="text-xs font-bold text-500 uppercase mb-2" style={{ letterSpacing: '.4px' }}>Current Team</div>
                                        {people.filter(p => p.role === 'editor' || p.role === 'super_admin').map(m => {
                                            const nameStr = m.name || m.full_name || '';
                                            const pid     = m.profile_id || m.id;
                                            const isOwner = m.role === 'super_admin';
                                            return (
                                                <div key={pid} className="flex align-items-center gap-3 p-2 border-round-lg mb-2 surface-50 border-1 surface-border">
                                                    <Avatar
                                                        label={initials(nameStr)}
                                                        shape="circle"
                                                        style={{ background: isOwner ? '#0A66C2' : '#7C3AED', color: '#fff', fontWeight: 700, width: '36px', height: '36px', fontSize: '13px' }}
                                                    />
                                                    <div className="flex-1 min-w-0">
                                                        <div className="font-semibold text-sm text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis">{nameStr}</div>
                                                        <div className="text-xs text-400">{m.email || ''}</div>
                                                    </div>
                                                    {!isOwner && (
                                                        <>
                                                            <Tag value="Editor" icon="pi pi-pencil" severity="info" className="text-xs" />
                                                            <Button icon="pi pi-times" rounded text severity="danger" size="small" tooltip="Remove access" tooltipOptions={{ position: 'left' }} onClick={() => removeMember(m)} />
                                                        </>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Add user as editor */}
                                <div className="text-xs font-bold text-500 uppercase mb-2" style={{ letterSpacing: '.4px' }}>Add a User as Editor</div>
                                <div className="flex gap-2 align-items-center">
                                    <Dropdown
                                        value={adminSelectedUser}
                                        options={adminUsers.filter(u => !people.find(p => (p.profile_id || p.id) === u.id && (p.role === 'editor' || p.role === 'super_admin' || p.role === 'admin')))}
                                        onChange={e => setAdminSelectedUser(e.value)}
                                        optionLabel="_display"
                                        placeholder={adminUsersLoading ? 'Loading users…' : adminUsers.length === 0 ? 'No users found' : 'Select a user…'}
                                        filter
                                        filterBy="_display,_sub"
                                        className="flex-1"
                                        disabled={adminUsersLoading || adminUsers.length === 0}
                                        emptyFilterMessage="No users match your search"
                                        itemTemplate={opt => (
                                            <div className="flex align-items-center gap-2 py-1">
                                                <Avatar
                                                    label={(opt._display || '?').slice(0, 2).toUpperCase()}
                                                    shape="circle"
                                                    style={{ background: 'var(--primary-100)', color: 'var(--primary-700)', fontWeight: 700, width: '28px', height: '28px', fontSize: '11px' }}
                                                />
                                                <div>
                                                    <div className="text-sm font-semibold text-900">{opt._display}</div>
                                                    {opt._sub && <div className="text-xs text-400">{opt._sub}</div>}
                                                    {opt._role && <div className="text-xs text-400" style={{ color: 'var(--primary-500)' }}>{opt._role}</div>}
                                                </div>
                                            </div>
                                        )}
                                    />
                                    <Button
                                        icon={addingUserEditor ? 'pi pi-spin pi-spinner' : 'pi pi-user-plus'}
                                        label="Add"
                                        size="small"
                                        disabled={!adminSelectedUser || addingUserEditor}
                                        onClick={addUserAsEditor}
                                    />
                                </div>
                                {adminUsersLoading && (
                                    <small className="text-400 mt-1 block"><i className="pi pi-spin pi-spinner mr-1" />Loading users…</small>
                                )}
                            </Card>

                            {/* Page Actions */}
                            <Card className="border-1 surface-border">
                                <div className="text-base font-bold text-900 mb-3">
                                    <i className="pi pi-shield mr-2 text-primary" />Page Actions
                                </div>
                                <div className="flex flex-column gap-2">
                                    <Button
                                        label="Deactivate Page"
                                        icon="pi pi-ban"
                                        outlined
                                        severity="warning"
                                        className="w-full justify-content-start"
                                        onClick={handleDeactivate}
                                    />
                                    <Button
                                        label="Delete Page"
                                        icon="pi pi-trash"
                                        severity="danger"
                                        className="w-full justify-content-start"
                                        onClick={handleDeletePage}
                                    />
                                </div>
                                <p className="text-xs text-400 mt-3 mb-0 line-height-3">
                                    <i className="pi pi-info-circle mr-1" />
                                    Deactivating hides the page temporarily. Deleting is permanent and cannot be undone.
                                </p>
                            </Card>
                        </div>
                    </TabPanel>
                )}
            </TabView>

            {/* ── Product Dialog ─────────────────────────────── */}
            <Dialog visible={showProduct} onHide={() => setShowProduct(false)} header={editProduct ? 'Edit Loan Product' : 'Add Loan Product'} style={{ width: '480px' }} breakpoints={{ '768px': '95vw' }} modal
                footer={
                    <div className="flex gap-2 justify-content-end">
                        <Button label="Cancel" outlined onClick={() => setShowProduct(false)} disabled={savingProd} />
                        <Button label={savingProd ? 'Saving…' : 'Save'} icon={savingProd ? 'pi pi-spin pi-spinner' : 'pi pi-check'} onClick={saveProduct} disabled={savingProd} />
                    </div>
                }
            >
                <div className="flex flex-column gap-3">
                    <div className="field"><label className="text-sm font-medium text-700 block mb-1">Loan Type</label>
                        <Dropdown value={prodForm.loan_type} options={Object.entries(LOAN_TYPE_META).map(([k, v]) => ({ label: v.label, value: k }))} onChange={e => setProdForm(p => ({ ...p, loan_type: e.value }))} className="w-full" />
                    </div>
                    <div className="field"><label className="text-sm font-medium text-700 block mb-1">Product Name</label>
                        <InputText value={prodForm.product_name || ''} onChange={e => setProdForm(p => ({ ...p, product_name: e.target.value }))} className="w-full" />
                    </div>
                    <div className="grid">
                        {[['min_amount','Min Amount (₹)'],['max_amount','Max Amount (₹)'],['min_roi','Min ROI (%)'],['max_roi','Max ROI (%)'],['min_tenure','Min Tenure (months)'],['max_tenure','Max Tenure (months)']].map(([field, label]) => (
                            <div key={field} className="col-6 field">
                                <label className="text-sm font-medium text-700 block mb-1">{label}</label>
                                <InputNumber value={prodForm[field]} onValueChange={e => setProdForm(p => ({ ...p, [field]: e.value }))} className="w-full" minFractionDigits={field.includes('roi') ? 1 : 0} maxFractionDigits={field.includes('roi') ? 2 : 0} />
                            </div>
                        ))}
                    </div>
                </div>
            </Dialog>

            {/* ── Add Member Dialog (uses same leads list) ───── */}
            <Dialog visible={showAddMember} onHide={() => { setShowAddMember(false); setSelectedUser(null); }} header="Add Editor Access" style={{ width: '420px' }} breakpoints={{ '768px': '95vw' }} modal
                footer={
                    <div className="flex gap-2 justify-content-end">
                        <Button label="Cancel" outlined onClick={() => { setShowAddMember(false); setSelectedUser(null); }} disabled={addingMember} />
                        <Button label={addingMember ? 'Adding…' : 'Add as Editor'} icon={addingMember ? 'pi pi-spin pi-spinner' : 'pi pi-user-plus'} onClick={addMember} disabled={!selectedUser || addingMember} />
                    </div>
                }
            >
                <div className="flex flex-column gap-3">
                    <div className="flex align-items-center gap-2 p-3 border-round-lg" style={{ background: 'var(--blue-50)', border: '1px solid var(--blue-200)' }}>
                        <i className="pi pi-info-circle text-primary" />
                        <span className="text-sm text-700">Selected users will be granted <strong>Editor</strong> access to this page.</span>
                    </div>
                    <div className="field mb-0">
                        <label className="text-sm font-medium text-700 block mb-2">Select a User</label>
                        <Dropdown
                            value={selectedUser}
                            options={adminUsers.filter(u => !people.find(p => (p.profile_id || p.id) === u.id && (p.role === 'editor' || p.role === 'super_admin' || p.role === 'admin')))}
                            onChange={e => setSelectedUser(e.value)}
                            optionLabel="_display"
                            placeholder={adminUsersLoading ? 'Loading users…' : 'Select a user…'}
                            filter
                            filterBy="_display,_sub"
                            className="w-full"
                            disabled={adminUsersLoading}
                            emptyFilterMessage="No users match"
                            itemTemplate={opt => (
                                <div className="flex align-items-center gap-2 py-1">
                                    <Avatar label={(opt._display || '?').slice(0, 2).toUpperCase()} shape="circle"
                                        style={{ background: 'var(--primary-100)', color: 'var(--primary-700)', fontWeight: 700, width: '28px', height: '28px', fontSize: '11px' }} />
                                    <div>
                                        <div className="text-sm font-semibold">{opt._display}</div>
                                        {opt._sub && <div className="text-xs text-400">{opt._sub}</div>}
                                    </div>
                                </div>
                            )}
                        />
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default CompanyPageDetail;
